import React, { useEffect, useMemo, useState } from 'react';
import { useAdminMaintenance } from '../../hooks/useAdminMaintenance.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const formatUpdatedAt = (value) => {
  if (!value) return 'Sin cambios registrados';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Actualización reciente';
  return new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(parsed);
};

const MaintenanceSection = ({ onNotify }) => {
  const { settings, loading, saving, error, loadSettings, saveSettings } = useAdminMaintenance();
  const [formValues, setFormValues] = useState(settings);

  useEffect(() => setFormValues(settings), [settings]);

  const normalizedMessage = String(formValues.message || '').trim();
  const isDirty = useMemo(() => (
    Boolean(formValues.enabled) !== settings.enabled
    || normalizedMessage !== settings.message
  ), [formValues.enabled, normalizedMessage, settings.enabled, settings.message]);

  useLucideIcons([loading, saving, formValues.enabled, isDirty, settings.updated_at]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const next = await saveSettings({ ...formValues, message: normalizedMessage });
      onNotify?.({
        type: next.enabled ? 'warning' : 'success',
        message: next.enabled
          ? 'Modo de mantenimiento activado. Los usuarios ya ven el aviso.'
          : 'Modo de mantenimiento desactivado. El acceso normal fue restaurado.',
        duration: 6000
      });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar el cambio.' });
    }
  };

  const handleRefresh = async () => {
    try {
      const next = await loadSettings();
      setFormValues(next);
      onNotify?.({ type: 'success', message: 'Estado del sistema actualizado.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo actualizar el estado.' });
    }
  };

  return (
    <section id="maintenance-section" className="admin-section active maintenance-admin-section">
      <div className="section-header">
        <div className="section-title-group">
          <div className="section-eyebrow">Disponibilidad</div>
          <h2 className="section-title">Modo de mantenimiento</h2>
          <p className="section-subtitle">
            Pausa el acceso operativo mientras se realizan cambios. Los super administradores conservan el acceso.
          </p>
        </div>
        <div className="section-actions">
          <button className="btn-ghost" type="button" onClick={handleRefresh} disabled={loading || saving}>
            <span className="btn-icon"><i data-lucide="refresh-cw" /></span>
            {loading ? 'Consultando...' : 'Actualizar estado'}
          </button>
        </div>
      </div>

      <div className={`maintenance-status-card ${formValues.enabled ? 'is-active' : 'is-available'}`}>
        <div className="maintenance-status-signal" aria-hidden="true">
          <span className="maintenance-signal-ring" />
          <i data-lucide={formValues.enabled ? 'construction' : 'circle-check-big'} />
        </div>
        <div className="maintenance-status-copy">
          <span className="maintenance-status-kicker">Estado actual</span>
          <h3>{formValues.enabled ? 'Acceso pausado para usuarios' : 'Sistema disponible'}</h3>
          <p>
            {formValues.enabled
              ? 'El aviso de mantenimiento está visible. Las funciones operativas están bloqueadas.'
              : 'Los usuarios pueden entrar y utilizar todas las funciones que tienen autorizadas.'}
          </p>
        </div>
        <label className="maintenance-master-switch">
          <span>{formValues.enabled ? 'Activado' : 'Desactivado'}</span>
          <span className="ios-switch">
            <input
              type="checkbox"
              checked={Boolean(formValues.enabled)}
              onChange={(event) => setFormValues((current) => ({
                ...current,
                enabled: event.target.checked
              }))}
              disabled={loading || saving}
              aria-label="Activar modo de mantenimiento"
            />
            <span className="ios-switch-slider" />
          </span>
        </label>
      </div>

      <div className="maintenance-config-grid">
        <div className="dashboard-card maintenance-message-card">
          <div className="card-header">
            <h3 className="card-title"><i data-lucide="message-square-text" /> Mensaje para usuarios</h3>
          </div>
          <div className="card-content">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner" />
                <div className="loading-text">Cargando configuración...</div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="maintenance-message">Mensaje personalizado</label>
                  <textarea
                    id="maintenance-message"
                    className="form-input maintenance-message-input"
                    value={formValues.message || ''}
                    onChange={(event) => setFormValues((current) => ({
                      ...current,
                      message: event.target.value
                    }))}
                    maxLength="1000"
                    rows="5"
                    required
                  />
                  <div className="maintenance-field-meta">
                    <span>Explica brevemente qué sucede y cuándo deben volver a intentar.</span>
                    <span>{String(formValues.message || '').length}/1000</span>
                  </div>
                </div>
                {error ? <div className="form-error" role="alert">{error}</div> : null}
                <div className="maintenance-form-actions">
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={() => setFormValues(settings)}
                    disabled={!isDirty || saving}
                  >
                    Descartar cambios
                  </button>
                  <button
                    className={formValues.enabled ? 'btn-maintenance' : 'btn-primary'}
                    type="submit"
                    disabled={!isDirty || saving || !normalizedMessage}
                  >
                    {saving ? 'Guardando...' : formValues.enabled ? 'Guardar y activar' : 'Guardar configuración'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <aside className="maintenance-preview-card">
          <span className="maintenance-preview-label">Vista previa</span>
          <div className="maintenance-preview-icon"><i data-lucide="wrench" /></div>
          <h3>Volvemos pronto</h3>
          <p>{normalizedMessage || 'El mensaje personalizado aparecerá aquí.'}</p>
          <div className="maintenance-preview-access">
            <i data-lucide="shield-check" />
            <span>El acceso administrativo seguirá disponible.</span>
          </div>
          <div className="maintenance-audit">
            <span>Último cambio</span>
            <strong>{formatUpdatedAt(settings.updated_at)}</strong>
            {settings.updated_by ? <small>{settings.updated_by}</small> : null}
          </div>
        </aside>
      </div>
    </section>
  );
};

export default MaintenanceSection;
